import ResetPassword from '@/components/shared/ResetPassword';
import { ApiProvider } from '@/provider/ApiProvider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Typography } from '@mui/material';
import Menu from '@mui/material/Menu';
import React, { useState, useEffect } from 'react';
import SidebarLink from '@/components/shared/Sidebar/SidebarLink';
import {
  People as PeopleIcon,
  HelpOutline as HelpIcon,
  AccountCircle as AccountIcon,
  Security as SecurityIcon,
  LockReset as ResetPasswordIcon,
  Logout as LogoutIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { basicLinkStyles } from '@/components/shared/Sidebar/styles';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useNavigate } from 'react-router-dom';

export default function SimpleMenu({ anchorElOutside }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [defaultPagePopup, setDefaultPagePopup] = useState(false);
  const [openrightpopup, setResetPopupOpen] = useState();
  const [defaultHomePageValue, setDefaultHomePageValue] = useState('');
  const [defaultBrandPageValue, setDefaultBrandPageValue] = useState('');
  const [defaultPropertyPageValue, setDefaultPropertyPageValue] = useState('');
  const userData = ApiProvider.returnUserData();
  const email = userData?.email || '';
  const name = `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim();
  const helpUrl =
    'https://forms.zohopublic.eu/advancedsponsorshipinsights/form/PartnershipNet/formperma/AnxHopJYvClqFRqVKjSgdNb6-zG_ikPJTFVnyt7yaO0' +
    `?name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}`;

  function openSecurityPage() {
    setAnchorEl(null);
    navigate('/security');
  }

  useEffect(() => {
    if (anchorElOutside) {
      setAnchorEl(anchorElOutside);
    }
  }, [anchorElOutside]);

  const links = [
    {
      text: 'Training',
      to: 'https://asierp.zohodesk.eu/portal/en/kb/asi',
      icon: PeopleIcon,
      target: '_blank',
      isExternal: true,
    },
    {
      text: 'Help',
      to: helpUrl,
      icon: HelpIcon,
      target: '_blank',
      isExternal: true,
    },
    {
      text: 'Set Your Default Landing Page',
      handleClick: () => handleSetDefaultPagePopup(),
      icon: CheckCircleIcon,
    },
    {
      text: 'Account',
      to: '/adduser/edit/' + ApiProvider.returnUserData()?.id,
      handleClick: () => handleCloserefresh(),
      icon: AccountIcon,
    },
    {
      text: 'Security',
      handleClick: () => openSecurityPage(),
      icon: SecurityIcon,
    },
    ...(ApiProvider.returnUserData()?.loginusertype === 'password'
      ? [
        {
          text: 'Reset Password',
          handleClick: () => handleClickOpen(),
          icon: ResetPasswordIcon,
        },
      ]
      : []),
    {
      text: 'Logout',
      handleClick: () => logouthandleClose(),
      icon: LogoutIcon,
    },
  ];

  const handleClosePopup = () => {
    setOpen(false);
  };

  const logout = async () => {
    setOpen(false);

    // Best-effort server logout for the current device session. If the local JWT
    // is already expired/revoked, still complete client-side sign-out quietly.
    try {
      let body = new FormData();
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        body.append('refresh_token', refreshToken);
      }
      await ApiProvider.webservicePostToken('Logout', body, 'yes', null, {
        suppressUnauthorizedRedirect: true,
      });
    } catch (error) {
      // Ignore - logoutUserLocally below is the source of truth for client sign-out.
    }

    await ApiProvider.logoutUserLocally(true);
  };

  const logouthandleClose = () => {
    setAnchorEl(null);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSetDefaultPagePopup = () => {
    if (ApiProvider.returnUserData()?.brand_module) {
      setDefaultBrandPageValue(ApiProvider.returnUserData()?.brand_module.id);
    }
    if (ApiProvider.returnUserData()?.property_module) {
      setDefaultPropertyPageValue(ApiProvider.returnUserData()?.property_module.id);
    }
    if (ApiProvider.returnUserData()?.home_module) {
      setDefaultHomePageValue(ApiProvider.returnUserData()?.home_module.id);
    }
    setDefaultPagePopup(true);
  };

  const handleCloseDefaultPagePopup = () => {
    setDefaultHomePageValue('');
    setDefaultBrandPageValue('');
    setDefaultPropertyPageValue('');
    setDefaultPagePopup(false);
  };

  function findModuleById(modules, id) {
    // Iterate through each module (modules can be null/undefined when
    // useraccess isn't in localStorage — returnUserAccessData() returns null).
    for (const module of modules || []) {
      // If the id matches the module, return it
      if (module.id == id) {
        return module;
      }

      // If sub_module exists, search within it
      if (module.sub_module && module.sub_module.length > 0) {
        const subModuleResult = findModuleById(module.sub_module, id);
        if (subModuleResult) {
          return subModuleResult;
        }
      }
    }

    // If no match found, return null
    return null;
  }

  const saveDefaultPagePopup = () => {
    let body = new FormData();

    if (defaultHomePageValue) {
      body.append('home_module_id', defaultHomePageValue);
    }
    if (defaultBrandPageValue) {
      body.append('brand_module_id', defaultBrandPageValue);
    }
    if (defaultPropertyPageValue) {
      body.append('property_module_id', defaultPropertyPageValue);
    }

    ApiProvider.webservicePostToken('User/Redirect', body, 'yes').then(
      (response) => {
        if (response.success) {
          setAnchorEl(null);

          const userData = ApiProvider.returnUserData();

          // Update brand_module if defaultBrandPageValue exists, else reset
          userData['home_module'] = defaultHomePageValue
            ? findModuleById(ApiProvider.returnUserAccessData()?.home_module, defaultHomePageValue)
            : '';

          userData['brand_module'] = defaultBrandPageValue
            ? findModuleById(ApiProvider.returnUserAccessData()?.brand_module, defaultBrandPageValue)
            : '';

          // Update property_module if defaultPropertyPageValue exists, else reset
          userData['property_module'] = defaultPropertyPageValue
            ? findModuleById(
              ApiProvider.returnUserAccessData()?.property_module,
              defaultPropertyPageValue,
            )
            : '';

          localStorage.setItem('userData', JSON.stringify(userData));

          // Reset state values
          setDefaultHomePageValue('');
          setDefaultBrandPageValue('');
          setDefaultPropertyPageValue('');
          setDefaultPagePopup(false);

          ApiProvider.globalToastMessage(response.message, 5000, 'success');
        } else {
          ApiProvider.globalApiErrorMessage(response.message);
        }
      },
      (error) => {
        ApiProvider.globalToastMessage(error, 5000, 'error');
      },
    );
  };

  const defaultBrandPageChange = (event) => {
    setDefaultBrandPageValue(event.target.value);
  };

  const defaultPropertyPageChange = (event) => {
    setDefaultPropertyPageValue(event.target.value);
  };

  const handleCloserefresh = () => {
    setAnchorEl(null);
  };

  const handleClickOpen = () => {
    setResetPopupOpen('reset_password');
    setAnchorEl(null);
    setTimeout(() => {
      setResetPopupOpen('');
    }, 2000);
  };

  return (
    <div className='primary_color drop_arrow'>
      <Dialog
        className='default_landing_page_popup'
        open={defaultPagePopup}
        onClose={handleClosePopup}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>Set Your Default Landing Page</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            <Typography variant='body1' className='capitalize_text bold_font margin_bhalf'>
              For Homepage
            </Typography>
            <FormControl sx={{ mt: 1, mb: 1, minWidth: '100%' }}>
              <InputLabel shrink htmlFor='grouped-native-select-home'
                sx={{
                  backgroundColor: 'background.paper',
                  px: 0.5,
                }}
              >Select Page For Home</InputLabel>
              <Select
                value={defaultHomePageValue}
                onChange={(event) => setDefaultHomePageValue(event.target.value)}
                native
                id='grouped-native-select-home'
                label='Select Page For Home'
              >
                <option aria-label='System Default' value='' >System Default</option>
                {ApiProvider.returnUserAccessData() &&
                  ApiProvider.returnUserAccessData()?.home_module &&
                  ApiProvider.returnUserAccessData()?.home_module.map((row, i) => (
                    <React.Fragment key={i}>
                      {row.sub_module && row.sub_module.length > 0 ? (
                        <optgroup label={row.name}>
                          {row.sub_module.map((row1, i1) => (
                            <React.Fragment key={i1}>
                              <option value={row1.id}>{row1.name}</option>
                            </React.Fragment>
                          ))}
                        </optgroup>
                      ) : (
                        <option label={row.name} value={row.id}>
                          {row.name}
                        </option>
                      )}
                    </React.Fragment>
                  ))}
              </Select>
            </FormControl>

            <Typography variant='body1' className='capitalize_text bold_font margin_bhalf'>
              For Brand
            </Typography>
            <FormControl sx={{ m: 1, minWidth: '100%' }}>
              <InputLabel shrink htmlFor='grouped-native-select-brand' sx={{
                backgroundColor: 'background.paper',
                px: 0.5,
              }}>Select Page For Brand</InputLabel>
              <Select
                value={defaultBrandPageValue}
                onChange={defaultBrandPageChange}
                native
                id='grouped-native-select-brand'
                label='Select Page For Brand'
              >
                <option aria-label='System Default' value=''>System Default</option>
                {ApiProvider.returnUserAccessData() &&
                  ApiProvider.returnUserAccessData()?.brand_module &&
                  ApiProvider.returnUserAccessData()?.brand_module.map((row, i) => (
                    <React.Fragment key={i}>
                      {row.sub_module && row.sub_module.length > 0 ? (
                        <optgroup label={row.name}>
                          {row.sub_module.map((row1, i1) => (
                            <React.Fragment key={i1}>
                              <option value={row1.id}>{row1.name}</option>
                            </React.Fragment>
                          ))}
                        </optgroup>
                      ) : (
                        <option label={row.name} value={row.id}>
                          {row.name}
                        </option>
                      )}
                    </React.Fragment>
                  ))}
              </Select>
            </FormControl>
            <Typography
              variant='body1'
              className='capitalize_text bold_font margin_top2 margin_bhalf'
            >
              For Right Holder/Partner
            </Typography>
            <FormControl sx={{ m: 1, minWidth: '100%' }}>
              <InputLabel shrink htmlFor='grouped-native-select-property'
                sx={{
                  backgroundColor: 'background.paper',
                  px: 0.5,
                }}
              >Select Page For Right Holder/Partner</InputLabel>
              <Select
                value={defaultPropertyPageValue}
                onChange={defaultPropertyPageChange}
                native
                id='grouped-native-select-property'
                label='Select Page For Right Holder/Partner'
              >
                <option aria-label='System Default' value='' >System Default</option>
                {ApiProvider.returnUserAccessData() &&
                  ApiProvider.returnUserAccessData()?.property_module &&
                  ApiProvider.returnUserAccessData()?.property_module.map((row, i) => (
                    <React.Fragment key={i}>
                      {row.sub_module && row.sub_module.length > 0 ? (
                        <optgroup label={row.name}>
                          {row.sub_module.map((row1, i1) => (
                            <React.Fragment key={i1}>
                              <option value={row1.id}>{row1.name}</option>
                            </React.Fragment>
                          ))}
                        </optgroup>
                      ) : (
                        <option label={row.name} value={row.id}>
                          {row.name}
                        </option>
                      )}
                    </React.Fragment>
                  ))}
              </Select>
            </FormControl>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCloseDefaultPagePopup()} color='primary'>
            Cancel
          </Button>
          <Button onClick={() => saveDefaultPagePopup()} color='primary'>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={handleClosePopup}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>{'Logout'}</DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Are you sure you want to logout?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup} color='primary'>
            No
          </Button>
          <Button onClick={logout} color='primary' autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        id='simple-menu'
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{
          '.MuiPaper-root': {
            top: 'unset !important',
            borderRadius: '0',
            bottom: '0',
            backgroundColor: 'primary.main',
            padding: '10px 30px 10px 10px',
            boxShadow: 'none',
          },
          left: '49px',
          zIndex: '1201',
        }}
      >
        {links.map(({ to, handleClick, icon, text, target, isExternal }, index) => (
          <SidebarLink
            to={to}
            target={target}
            key={text + index}
            handleClick={handleClick}
            icon={icon}
            text={text}
            isExternal={isExternal}
            boxContainerStyles={{ padding: '8px 0', 'button, a': { gap: '10px' }, basicLinkStyles }}
          />
        ))}
        <ResetPassword openpopup={openrightpopup} sendpagename={'profiledropdown'} />
      </Menu>
    </div>
  );
}
